'use client';

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, Shield } from 'lucide-react';

export interface RoleContextSelectorProps {
  selectedRoles: string[];
  availableRoles?: string[];
  onRolesChange: (roles: string[]) => void;
}

export function RoleContextSelector({
  selectedRoles,
  availableRoles = ['admin', 'manager', 'developer', 'member', 'viewer', 'billing'],
  onRolesChange
}: RoleContextSelectorProps) {
  const toggleRole = (role: string) => {
    if (selectedRoles.includes(role)) {
      onRolesChange(selectedRoles.filter(r => r !== role));
    } else {
      onRolesChange([...selectedRoles, role]);
    }
  };

  const clearAll = () => {
    onRolesChange([]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
          <Shield className="h-4 w-4" />
          Simulate with Roles
        </label>
        {selectedRoles.length > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAll} className="h-6 text-xs">
            Clear all
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border">
        {availableRoles.map(role => {
          const isSelected = selectedRoles.includes(role);
          return (
            <Badge
              key={role}
              variant={isSelected ? 'default' : 'outline'}
              className={`cursor-pointer transition-all capitalize ${
                isSelected
                  ? 'bg-indigo-600 hover:bg-indigo-700'
                  : 'hover:bg-slate-100'
              }`}
              onClick={() => toggleRole(role)}
            >
              {role}
              {isSelected && <X className="h-3 w-3 ml-1" />}
            </Badge>
          );
        })}
      </div>
      {selectedRoles.length > 0 && (
        <p className="text-xs text-slate-500">
          Simulating as user with roles: {selectedRoles.join(', ')}
        </p>
      )}
    </div>
  );
}
